/**
 * ConnectionNode — Node visual "Conexão" do Flow Builder
 * -----------------------------------------------------------------------------
 * Consulta um provider integrado (Integration Hub) dentro de um fluxo.
 * A execução é assíncrona: o motor enfileira na fila `q_integration` e o worker
 * devolve o retorno em variáveis do fluxo (JSON + URL do PDF, quando houver).
 *
 * RELEASE: 1.0.0 - 2026-08-29 - Versão inicial (Fase 2 da Fila de Integrações).
 */

import { Handle, Position, NodeProps } from '@xyflow/react';
import { Plug, Database, Variable } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { FlowNodeData, ConnectionQueryConfig } from '@/types/flows';
import { FlowNodeToolbar } from './FlowNodeToolbar';

export function ConnectionNode({ data, id, selected }: NodeProps) {
  const nodeData = data as FlowNodeData;
  const config = nodeData.config as ConnectionQueryConfig | undefined;

  // O node só é válido quando conexão e recurso estão escolhidos
  const isConfigured = Boolean(config?.connection_id && config?.operation);

  return (
    <>
      <FlowNodeToolbar
        nodeId={id}
        nodeType={nodeData.type}
        onEdit={nodeData.onEdit as (() => void) | undefined}
        onDuplicate={(nodeData as any).onDuplicate as (() => void) | undefined}
        onDelete={nodeData.onDelete as (() => void) | undefined}
        onTestFromHere={(nodeData as any).onTestFromHere as (() => void) | undefined}
        isVisible={selected}
      />

      {/* Entrada */}
      <Handle
        type="target"
        position={Position.Top}
        className="!w-3 !h-3 !bg-sky-500 !border-2 !border-white"
      />

      <div
        className={`
          min-w-[220px] max-w-[280px] bg-card rounded-lg border-2 shadow-lg
          ${selected ? 'border-sky-500 ring-2 ring-sky-500/30' : 'border-sky-400'}
          transition-all duration-200
        `}
      >
        {/* Cabeçalho */}
        <div className="flex items-center gap-2 p-3 border-b border-border bg-sky-500/10 rounded-t-lg">
          <div className="p-1.5 rounded bg-sky-500">
            <Plug className="h-4 w-4 text-white" />
          </div>
          <span className="font-semibold text-sm flex-1">
            {config?.nickname || 'Conexão'}
          </span>
        </div>

        {/* Conteúdo */}
        <div className="p-3 space-y-2 text-xs">
          {isConfigured ? (
            <>
              <div className="flex items-center gap-1.5">
                <Database className="h-3 w-3 text-muted-foreground" />
                <span className="text-muted-foreground">Recurso:</span>
                <span className="font-medium truncate">
                  {config?.operation_name || config?.operation}
                </span>
              </div>

              {config?.connection_name && (
                <div className="flex items-center gap-1.5">
                  <Plug className="h-3 w-3 text-muted-foreground" />
                  <span className="text-muted-foreground">Conexão:</span>
                  <span className="font-medium truncate">{config.connection_name}</span>
                </div>
              )}

              <div className="flex items-center gap-1.5">
                <Variable className="h-3 w-3 text-muted-foreground" />
                <span className="text-muted-foreground">Retorno em:</span>
                <span className="font-mono">{`{{${config?.result_variable || 'consulta'}}}`}</span>
              </div>
            </>
          ) : (
            <Badge variant="outline" className="text-[10px]">
              Clique para configurar
            </Badge>
          )}
        </div>
      </div>

      {/* Saída */}
      <Handle
        type="source"
        position={Position.Bottom}
        className="!w-3 !h-3 !bg-sky-500 !border-2 !border-white"
      />
    </>
  );
}
